import {
  BrowserMessageType,
  sendElectronMessage,
} from 'electron-communication-lib/BrowserForApp';
import {
  action,
  AnnotationsMap,
  makeObservable,
  observable,
  override,
  runInAction,
} from 'mobx';

import documentsHandledBackend, {
  DefaultValueInResponse,
  EmailAddress,
  EmailFromTemplateCreationInParams,
  TemplateFileInResponse,
  TemplateInResponse,
} from '@lexian/api/documents-api';
import {TranslatedLocale} from '@lexian/api/support-api';
import {LoggedInUserInformation} from '@lexian/api/user-async-backend';
import {RecipientsFormFieldValues} from '_/sharedComponents/CreateFromTemplateWizard/RecipientsForm';
import {State, unexpectedError} from '_/utilities';
import {isNotNullOrUndefined, Loadable} from '_/utilities/types';

import {AttachmentsType, ChooseTemplateData, Recipient} from './template.types';
import {getCreateTemporaryDocumentParams} from './template.utils';
import TemplateBaseStore from './templateBase.store';

const formatRecipients = (recipients: Recipient[]): EmailAddress[] =>
  recipients.map((recipient) => ({
    emailAddress: recipient.emailAddress,
    name: recipient.contactName,
  }));

const createEmailFromTemplateStoreMobXAnnotations: AnnotationsMap<
  CreateEmailFromTemplateStore,
  'markTemporaryDocumentAsReady'
> = {
  templateLoadable: observable,
  chooseTemplateData: observable,
  toRecipients: observable,
  ccRecipients: observable,
  bccRecipients: observable,
  attachments: observable,
  setChooseTemplateData: action.bound,
  getTranslatedLocales: action.bound,
  getOverridableTemplatePlaceholders: action.bound,
  fetchDependablePlaceholderValues: action.bound,
  createTemporaryDocumentFromTemplate: action.bound,
  updateRecipients: action.bound,
  markTemporaryDocumentAsReady: override,
};

export default class CreateEmailFromTemplateStore extends TemplateBaseStore {
  private readonly locales: TranslatedLocale[];

  public templateLoadable: Loadable<TemplateInResponse[]>;

  public chooseTemplateData: ChooseTemplateData;

  public toRecipients: Recipient[] = [];
  public ccRecipients: Recipient[] = [];
  public bccRecipients: Recipient[] = [];
  public attachments?: AttachmentsType;

  public constructor({
    attachments,
    casusId,
    loggedInUser,
    locales,
  }: {
    attachments?: AttachmentsType;
    casusId?: string;
    loggedInUser: LoggedInUserInformation;
    locales: TranslatedLocale[];
  }) {
    super({
      loggedInUser,
      includeRecipientStep: true,
    });

    makeObservable(this, createEmailFromTemplateStoreMobXAnnotations);

    this.locales = locales;

    this.templateLoadable = {state: State.Success};
    this.getTemplates();

    this.chooseTemplateData = {casusId};
    this.attachments = attachments;
  }

  public setChooseTemplateData({
    casusId,
    templateId,
    localeId,
  }: Required<Omit<ChooseTemplateData, 'templateFileId'>>): void {
    const templateFileId = this.getTemplateFile(templateId, localeId)?.id;

    if (!templateFileId) {
      throw Error('Invalid template file id');
    }
    this.chooseTemplateData = {casusId, templateId, localeId, templateFileId};
  }

  private getTemplateFile(
    templateId: string,
    localeId: string
  ): TemplateFileInResponse | undefined {
    const templateFiles =
      this.templateLoadable.data?.find(({id}) => id === templateId)
        ?.templateFiles ?? [];
    return templateFiles.find(({locale}) => locale === localeId);
  }

  public getTranslatedLocales(templateId: string): TranslatedLocale[] {
    const templateFiles = this.templateLoadable.data?.find(
      ({id}) => id === templateId
    )?.templateFiles;

    return (
      templateFiles
        ?.map((file) => this.locales.find(({locale}) => locale === file.locale))
        .filter(isNotNullOrUndefined) ?? []
    );
  }

  private async getTemplates(): Promise<void> {
    this.templateLoadable.state = State.Loading;
    this.templateLoadable.abort?.();

    await documentsHandledBackend.getEmailTemplates({
      parameters: {},
      abortCallbackSetter: (abortCallback) => {
        this.templateLoadable.abort = abortCallback;
      },
      onSuccess: ({body}) => {
        runInAction(() => {
          this.templateLoadable.data = body.templates;
          this.templateLoadable.state = State.Success;
        });
      },
      afterErrorHandler: () => {
        runInAction(() => {
          this.templateLoadable.state = State.Error;
        });
      },
    });
  }

  private getTemplateData(): Required<ChooseTemplateData> {
    if (
      !this.chooseTemplateData.templateFileId ||
      !this.chooseTemplateData.casusId ||
      !this.chooseTemplateData.localeId ||
      !this.chooseTemplateData.templateId
    ) {
      throw new Error('Required params are undefined');
    }

    return {
      casusId: this.chooseTemplateData.casusId,
      templateId: this.chooseTemplateData.templateId,
      localeId: this.chooseTemplateData.localeId,
      templateFileId: this.chooseTemplateData.templateFileId,
    };
  }

  public async getOverridableTemplatePlaceholders(): Promise<void> {
    const {casusId, localeId, templateId, templateFileId} =
      this.getTemplateData();
    await documentsHandledBackend.getOverridableTemplatePlaceholders({
      parameters: {
        casusId,
        locale: localeId,
        templateFileId,
        templateId,
      },
      onSuccess: ({body}) => {
        super.getOverridableTemplatePlaceholders(body);
      },
      afterErrorHandler: () => {
        runInAction(() => {
          this.placeholderLoader.state = State.Error;
        });
      },
    });
  }

  public fetchDependablePlaceholderValues(params: {
    pageKey: string;
    parentPlaceholderKey: string;
    parentEntityId: string;
    placeholderKey: string;
  }): Promise<DefaultValueInResponse[] | void> {
    try {
      const {casusId, localeId, templateId, templateFileId} =
        this.getTemplateData();
      return super.fetchDependablePlaceholderValues({
        ...params,
        locale: localeId,
        casusId,
        templateId,
        templateFileId,
      });
    } catch {
      unexpectedError();
      return Promise.resolve();
    }
  }

  private get emailTemplateParams(): EmailFromTemplateCreationInParams {
    return {
      attachments: this.attachments,
      recipients: {
        bcc: formatRecipients(this.bccRecipients),
        cc: formatRecipients(this.ccRecipients),
        to: formatRecipients(this.toRecipients),
      },
    };
  }

  public async createTemporaryDocumentFromTemplate(): Promise<void> {
    const {casusId, localeId, templateFileId, templateId} =
      this.getTemplateData();
    await documentsHandledBackend.createTemporaryDocumentFromTemplate({
      parameters: {
        body: {
          ...getCreateTemporaryDocumentParams(
            this.placeholderLoader.data ?? []
          ),
          emailTemplateParams: this.emailTemplateParams,
          casusId,
          templateId,
          templateFileId,
          locale: localeId,
          async: true,
        },
      },
      onSuccess: ({body}) => {
        super.createTemporaryDocument(body);
      },
      afterErrorHandler: () => {
        runInAction(() => (this.temporaryDocumentDataState = State.Error));
      },
    });
  }

  public updateRecipients({
    toRecipients,
    ccRecipients,
    bccRecipients,
  }: RecipientsFormFieldValues): void {
    this.toRecipients = toRecipients;
    this.ccRecipients = ccRecipients;
    this.bccRecipients = bccRecipients;
  }

  protected markTemporaryDocumentAsReady(): void {
    if (!this.temporaryDocumentId) {
      return;
    }
    this.temporaryDocumentDataState = State.Loading;

    sendElectronMessage({
      payload: {temporaryDocumentId: this.temporaryDocumentId},
      type: BrowserMessageType.OPEN_TEMPORARY_EMAIL,
    });
  }
}
